/* Libraries */
import React, { FC, useEffect, useCallback, useMemo } from 'react';
import { Instance } from 'mobx-state-tree';
import { snakeCase } from 'lodash';
import ReactTooltip from 'react-tooltip';

/* Hooks */
import usePageScroll from '~/src/hooks/usePageScroll';
import usePdfCourtFormsPagination from '~/src/hooks/usePdfCourtFormsPagination';

/* Components */
import Breadcrumb from '~/src/components/Breadcrumb';
import MemoizedPdfTable from '~/src/components/CourtForms/PdfTable';
import LoadMoreDisplayText from '~/src/components/LoadMoreDisplayText';
import Button from '~/src/components/Button';
import RowActions from '~/src/components/RowActions/';
import { ReactComponent as ItemSelectedIcon } from '~/src/static/item-selected.svg';

import { Actions } from '~/src/components/RowActions/RowActions';
import { CourtFormSortByOption } from '~/src/hooks/courtFormSortByOption';
import { SortByOrder } from '~/src/hooks/sortByOrder';
import { PdfCourtFormStore } from '~/src/stores/pdfCourtFormsStore';
import type { BreadcrumbDataItem } from '../Breadcrumb/Breadcrumb';

export interface PdfCourtForm extends Instance<typeof PdfCourtFormStore> {}

interface Props {
  stateCode: string;
  locality?: string;
  practiceArea?: string;
  region?: string;
  disableAddButton: boolean;
  breadcrumbData: BreadcrumbDataItem[];
  rowActions?: Actions<PdfCourtForm>;
  handleAddTemplateToSeletionSidebar: (t: PdfCourtForm) => void;
  handlePreviewTemplate: (t: PdfCourtForm) => void;
  containedInSidebar: (t: PdfCourtForm) => boolean;
  disableBreadcrumb?: boolean;
}

// Break down into smaller, focused components

const AddedIcon: React.FC = () => (
  <div className="flex">
    <div className="mr-2 flex items-center">
      <ItemSelectedIcon />
    </div>
    <div className="leading-none">Added</div>
  </div>
);

const AddButton: React.FC<{
  template: PdfCourtForm;
  handleAddTemplateToSeletionSidebar: (template: PdfCourtForm) => void;
  disabled: boolean;
}> = ({ template, handleAddTemplateToSeletionSidebar, disabled }) => {
  const handleClick = useCallback(() => {
    handleAddTemplateToSeletionSidebar(template);
  }, [handleAddTemplateToSeletionSidebar, template]);

  const getTooltipContent = useCallback(() => {
    return disabled ? 'Cannot add more than 20 documents to selection.' : null;
  }, [disabled]);

  return (
    <div>
      <Button
        data-tip
        data-for={`add-button-${template.id}`}
        onClick={handleClick}
        disabled={disabled}
      >
        <span>Add</span>

        <ReactTooltip
          id={`add-button-${template.id}`}
          place="bottom"
          effect="solid"
          getContent={getTooltipContent}
        ></ReactTooltip>
      </Button>
    </div>
  );
};

const AddButtonOrAddedIcon: React.FC<{
  template: PdfCourtForm;
  containedInSidebar: (template: PdfCourtForm) => boolean;
  handleAddTemplateToSeletionSidebar: (template: PdfCourtForm) => void;
  disableAddButton: boolean;
}> = ({
  template,
  containedInSidebar,
  handleAddTemplateToSeletionSidebar,
  disableAddButton,
}) => {
  if (containedInSidebar(template)) {
    return <AddedIcon />;
  }

  return (
    <AddButton
      template={template}
      handleAddTemplateToSeletionSidebar={handleAddTemplateToSeletionSidebar}
      disabled={disableAddButton}
    />
  );
};

const PreviewButton: React.FC<{
  template: PdfCourtForm;
  handlePreviewTemplate: (template: PdfCourtForm) => void;
}> = ({ template, handlePreviewTemplate }) => {
  const handleClick = useCallback(() => {
    handlePreviewTemplate(template);
  }, [handlePreviewTemplate, template]);

  return (
    <div>
      <Button
        data-tip
        data-for={`add-button-${template.id}`}
        onClick={handleClick}
      >
        <span>Preview</span>
      </Button>
    </div>
  );
};

export const PdfTableRowAction: React.FC<{
  template: PdfCourtForm;
  rowActions?: Actions<PdfCourtForm>;
  containedInSidebar: (template: PdfCourtForm) => boolean;
  handleAddTemplateToSeletionSidebar: (template: PdfCourtForm) => void;
  handlePreviewTemplate: (template: PdfCourtForm) => void;
  disabled: boolean;
}> = ({
  template,
  rowActions,
  containedInSidebar,
  handleAddTemplateToSeletionSidebar,
  handlePreviewTemplate,
  disabled,
}) => (
  <div className="flex">
    <div className="mr-6 flex items-center">
      <AddButtonOrAddedIcon
        template={template}
        containedInSidebar={containedInSidebar}
        handleAddTemplateToSeletionSidebar={handleAddTemplateToSeletionSidebar}
        disableAddButton={disabled}
      />
    </div>
    {rowActions ? (
      <RowActions actions={rowActions} template={template} />
    ) : (
      <PreviewButton
        template={template}
        handlePreviewTemplate={handlePreviewTemplate}
      />
    )}
  </div>
);

const PdfTableWrapper: FC<Props> = ({
  stateCode,
  locality,
  practiceArea,
  region,
  disableAddButton,
  breadcrumbData,
  rowActions,
  handleAddTemplateToSeletionSidebar,
  handlePreviewTemplate,
  containedInSidebar,
  disableBreadcrumb,
}) => {
  const requestPayload = {
    facets: {
      filter_category: stateCode,
    },
  };

  if (locality) {
    Object.assign(requestPayload.facets, { locality });
  }

  if (practiceArea) {
    Object.assign(requestPayload.facets, { legal_practice_area: practiceArea });
  }

  if (region) {
    Object.assign(requestPayload.facets, { region });
  }

  const { next, loading, done, data, setSearch, setSortBy, setOrder } =
    usePdfCourtFormsPagination(requestPayload);

  const pageScroll = usePageScroll();
  const loadMore = next;

  const handleOnPageScroll = () => {
    if ((pageScroll === 0 || pageScroll >= 85) && !loading) {
      loadMore();
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(handleOnPageScroll, [pageScroll]);

  const tableData = useMemo(() => {
    return data.map((pdfCourtForm: PdfCourtForm) => ({
      identifier: pdfCourtForm.identifier,
      id: pdfCourtForm.id,
      filePath: pdfCourtForm.filePath,
      fprint: pdfCourtForm.fprint,
      filterCategory: pdfCourtForm.filterCategory,
      locality: pdfCourtForm.locality,
      title: pdfCourtForm.title,
      subtitle: pdfCourtForm.subtitle,
      documentCategoryId: pdfCourtForm.documentCategoryId,
      legalDocumentType: pdfCourtForm.legalDocumentType,
      legalPracticeArea: pdfCourtForm.legalPracticeArea,
      isDeleted: pdfCourtForm.isDeleted,
    }));
  }, [data]);

  const handleSearchChange = useCallback(
    (searchValue: string) => {
      setSearch(searchValue);

      if (searchValue) {
        setSortBy(CourtFormSortByOption.title);
        setOrder(SortByOrder.asc);
      } else {
        setSortBy(CourtFormSortByOption.lastModified);
        setOrder(SortByOrder.desc);
      }
    },
    [setSearch, setSortBy, setOrder],
  );

  const handleSortByChange = useCallback(
    (property: string, order: number) => {
      if (!property) {
        return;
      }

      const orderLabel = order > 0 ? SortByOrder.desc : SortByOrder.asc;
      let sortBy = snakeCase(property);

      if (property === 'updatedAt') {
        sortBy = CourtFormSortByOption.lastModified;
      }

      setSortBy(sortBy);
      setOrder(orderLabel);
    },
    [setSortBy, setOrder],
  );

  const renderRowAction = useCallback(
    (template: PdfCourtForm) => (
      <PdfTableRowAction
        template={template}
        rowActions={rowActions}
        containedInSidebar={containedInSidebar}
        handleAddTemplateToSeletionSidebar={handleAddTemplateToSeletionSidebar}
        handlePreviewTemplate={handlePreviewTemplate}
        disabled={disableAddButton}
      />
    ),
    [
      rowActions,
      containedInSidebar,
      handleAddTemplateToSeletionSidebar,
      handlePreviewTemplate,
      disableAddButton,
    ],
  );

  return (
    <>
      {!disableBreadcrumb && <Breadcrumb data={breadcrumbData} />}
      <MemoizedPdfTable
        pdfCourtForms={tableData}
        visibleItems={tableData.length}
        empty={!loading && done && tableData.length === 0}
        renderRowAction={renderRowAction}
        onSearchChange={handleSearchChange}
        onSortByChange={handleSortByChange}
      />
      <LoadMoreDisplayText
        loading={loading}
        done={done}
        onLoadMore={loadMore}
      />
    </>
  );
};

export default PdfTableWrapper;
